import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react'
import SearchMapMarker from './Markers/SearchMapMarker'
import { getMarkersByOffers, decodeOfferIds } from './utils'
import { getPadding, parseBounds, subtractPaddingFromBounds, fitMarkersOnMap, useVisitedMarkersState } from '../../Search/SearchResultsMap/utils'
import debounce from 'lodash.debounce'
import { GoogleMap, GoogleMapProps, MarkerClusterer } from '@react-google-maps/api'
import { Cluster } from '@react-google-maps/marker-clusterer'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import { EnsureGoogleMapsLoaded } from 'contexts/GoogleMapsContext'
import useResponsiveSelector from 'hooks/useResponsiveSelector'
import { fitBoundsWithExtraSpace } from 'components/Common/PropertyMap/utils'
import { styles as clusterStyles } from 'components/Common/PropertyMap/Cluster'
import styled from 'styled-components'
import { replaceWithRegion } from 'actions/NavigationActions'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { parseSearchString } from 'lib/url/searchUrlUtils'
import ExperienceSearchMapCarousel from './ExperienceSearchMapCarousel'
import useSelectedItems from '../hooks/useSelectedItems'
import useExperiencesSearchFilters from '../hooks/useExperiencesSearchFilters'
import config from 'constants/config'

const MapWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  overflow: hidden;
`

const MAP_STYLE = {
  height: '100%',
  width: '100%',
  flexGrow: 1,
}

const MAP_OPTIONS: GoogleMapProps['options'] = {
  gestureHandling: 'greedy',
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  zoomControlOptions: {
    position: 3,
  },
  minZoom: 3,
  maxZoom: 19,
  mapId: config.GOOGLE_MAP_ID,
}

interface Props {
  windowSearch: string;
  filters: App.OfferListFilters;
  experiences: Array<App.ExperienceOffer>;
  fetching: boolean;
}

function ExperienceSearchMap(props: Props) {
  const { windowSearch, filters, fetching, experiences } = props

  const [map, setMap] = useState<google.maps.Map | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const ignoreNextIdle = useRef<boolean>(true)
  const hasInteracted = useRef<boolean>(false)
  // Determine whether the initial offers have loaded
  const initialOffersLoaded = useRef(false)
  const { setSelectedItems } = useSelectedItems()
  const [visitedMarkerIds, addVisitedMarkerId] = useVisitedMarkersState()

  const {
    setBoundsSearchFilter,
  } = useExperiencesSearchFilters()

  initialOffersLoaded.current = !fetching

  const isMobileSize = useResponsiveSelector({
    mobile: true,
    tablet: false,
    desktop: false,
    largeDesktop: false,
  })

  const onClusterClick = (cluster: Cluster) => {
    fitBoundsWithExtraSpace(map, cluster.getBounds())
  }

  const mapOptions = useMemo(() => ({
    ...MAP_OPTIONS,
    zoomControl: !isMobileSize,
  }), [isMobileSize])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const markers = useMemo(() => getMarkersByOffers(experiences), [experiences.length])
  const onInteract = useCallback(() => { hasInteracted.current = true }, [])

  const params = parseSearchString(windowSearch)

  // Get currently selected offer IDs
  const selectedOfferIds = useMemo(() => (
    decodeOfferIds(params.selectedOfferIds)
  ), [params.selectedOfferIds])

  const onMarkerClick = useCallback((marker: App.SearchExperienceMapMarker) => {
    if (marker.experienceItems) {
      setSelectedItems(marker.experienceItems)
    }
  }, [setSelectedItems])

  const onDeselectOffers = useCallback(() => {
    setSelectedItems([])
  }, [setSelectedItems])

  // Fit the correct bounds on load
  const onLoad = useCallback((map: google.maps.Map) => {
    ignoreNextIdle.current = true
    if (filters.bounds) {
      map.fitBounds(parseBounds(filters.bounds), getPadding(true))
    }
    else {
      if (selectedOfferIds.length > 0 && markers.length > 0) {
        const selectedMarker = markers.find((marker) => marker.id.split(',').includes(selectedOfferIds[0].id))
        if (selectedMarker) {
          fitMarkersOnMap(map, markers, selectedMarker)
        }
      } else {
        fitMarkersOnMap(map, markers)
      }
    }
    setMap(map)
  }, [filters.bounds, markers, selectedOfferIds])

  useEffect(() => {
    fitMarkersOnMap(map, markers)
  }, [markers, map])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  // Maintain mapping of `google.maps.Marker` instances to the source `App.SearchMapMarker` objects
  // so that the clusterer calculator can figure out what offers are in a cluster
  const markerMap = useRef(new Map<google.maps.Marker, App.SearchMapMarker>())
  const onMarkerLoad = useCallback((markerInstance: google.maps.Marker, markerData: App.SearchMapMarker) => {
    markerMap.current.set(markerInstance, markerData)
  }, [])

  const onMarkerUnmount = useCallback((markerInstance: google.maps.Marker) => {
    markerMap.current.delete(markerInstance)
  }, [])

  // Handle bounds search
  const onBoundsChanged = useCallback((newBounds: google.maps.LatLngBounds) => {
    if (!newBounds) { return }
    const bounds = newBounds.toUrlValue()
    setBoundsSearchFilter(bounds)
  }, [setBoundsSearchFilter])

  const onUserMovedMap = useMemo<() => void>(() => debounce(
    () => {
      if (!map || !containerRef.current) return
      const bounds = map.getBounds()
      if (!bounds) return
      const boundsWithoutMargin = subtractPaddingFromBounds(bounds, containerRef.current.getBoundingClientRect())
      onBoundsChanged(boundsWithoutMargin)
    },
    2000,
    { leading: true, trailing: true },
  ), [map, onBoundsChanged])

  const onIdle = useCallback(() => {
    if (!ignoreNextIdle.current && hasInteracted.current) {
      onUserMovedMap()
    }
    ignoreNextIdle.current = false
    hasInteracted.current = false
  }, [onUserMovedMap])

  const activeMarkerId = selectedOfferIds.map(selected => selected.id).join(',')
  useEffect(() => {
    if (activeMarkerId) {
      addVisitedMarkerId(activeMarkerId)
    }
  }, [activeMarkerId, addVisitedMarkerId])

  return (
    <MapWrapper ref={containerRef}>
      <EnsureGoogleMapsLoaded loadingElement={<LoadingIndicator inline />}>
        {!initialOffersLoaded.current && <LoadingIndicator inline />}
        {initialOffersLoaded.current && <GoogleMap
          id="le-map"
          zoom={17}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={onDeselectOffers}
          onDrag={onInteract}
          onZoomChanged={onInteract}
          onHeadingChanged={onInteract}
          onIdle={onIdle}
          mapContainerStyle={MAP_STYLE}
          options={mapOptions}
        >
          <MarkerClusterer
            averageCenter
            enableRetinaIcons
            gridSize={30}
            zoomOnClick
            onClick={onClusterClick}
            styles={[
              clusterStyles.black,
              clusterStyles.black,
            ]}
          >
            {clusterer => (
              <>
                {markers.map((marker) => (
                  <SearchMapMarker
                    key={marker.id}
                    marker={marker}
                    clusterer={clusterer}
                    onClick={onMarkerClick}
                    onLoad={onMarkerLoad}
                    onUnmount={onMarkerUnmount}
                    scaleDown={isMobileSize}
                    active={selectedOfferIds.some((offerId) => marker.id.split(',').includes(offerId.id))}
                    visited={visitedMarkerIds.has(marker.id)}
                  />
                ))}
              </>
            )}
          </MarkerClusterer>
        </GoogleMap>}
      </EnsureGoogleMapsLoaded>
      {!!experiences.length && <ExperienceSearchMapCarousel />}
    </MapWrapper>
  )
}

function mapStateToProps(state: App.State) {
  const filters = getUrlOfferListFilters(state)

  return {
    windowSearch: state.router.location.search,
    filters,
  }
}

const mapDispatchToProps = {
  replaceWithRegion,
}

export default connect(mapStateToProps, mapDispatchToProps)(ExperienceSearchMap)
